"use client";

import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useDebouncedCallback } from "use-debounce";
import { Paperclip } from "lucide-react";

import { useClassroomStore } from "../store/useClassroomStore";
import { NotificationSoundManager } from "../utils/notificationSound";

import { DragDropOverlay } from "@/features/chat/components/DragDropOverlay.tsx";
import { useChat } from "@/features/chat/hooks/useChat";
import { ConversationView } from "@/features/chat/components/ConversationView";
import { EmojiPickerButton } from "@/features/chat/components/EmojiPickerButton";
import { FilePreview } from "@/features/chat/components/FilePreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import useUserStore from "@/features/auth/store/useUserStore";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { WorkspaceBlockingIndicator } from "@/features/chat/components/WorkspaceBlockingIndicator";

interface ClassroomChatProps {
  isMobile: boolean;
  lessonId?: string;
}

export default function ClassroomChat({ isMobile }: ClassroomChatProps) {
  const { t } = useTranslation(["common", "chat", "classroom"]);
  const {
    isChatOpen,
    setIsChatOpen,
    unreadMessageCount,
    clearUnreadMessageCount,
    incrementUnreadMessageCount,
  } = useClassroomStore();
  const { data: workspaceData } = useWorkspaceInformationStore();
  const { user } = useUserStore();
  const MAX_FILE_COUNT = 5;

  const soundManager = useRef<NotificationSoundManager | null>(null);

  // Initialize sound manager
  useEffect(() => {
    soundManager.current = NotificationSoundManager.getInstance();
  }, []);

  // Callback for handling new messages from other users
  const handleNewMessageFromOthers = useCallback(
    (message: any) => {
      console.log("New message from other user:", message);

      // Only handle notifications when chat is closed
      if (!isChatOpen) {
        console.log("Chat is closed, incrementing unread count and playing notification");

        // Increment unread count using the store method
        incrementUnreadMessageCount();

        // Play notification sound
        if (soundManager.current) {
          console.log("Playing notification sound...");
          soundManager.current.playNotification();
        } else {
          console.warn("Sound manager not initialized");
        }
      } else {
        console.log("Chat is open, skipping notification");
      }
    },
    [isChatOpen, incrementUnreadMessageCount], // Added incrementUnreadMessageCount to dependencies
  );

  // Use the chat hook with the "lesson" type
  const {
    messageInput,
    selectedFiles,
    selectedFilesCount,
    isSending,
    isLoading,
    activeConversation,
    messages,
    sendMessage,
    handleKeyPress,
    handleInputChange,
    handleFileChange,
    removeFile,
    handleAttachmentClick,
    insertEmoji,
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handlePaste,
    openConversation,
    scrollToBottom,
    animatedMessageIds,
    cancelEditing,
    checkIfNearBottom,
    deleteMessage,
    editMessageContent,
    editTextareaRef,
    editingMessageId,
    editingPendingMessageId,
    fetchNextPage,
    formatFileSize,
    handleEditKeyPress,
    hasNextPage,
    isCurrentUser,
    isFetchingNextPage,

    saveEditedMessage,
    scrollAreaRef,
    startEditingMessage,
    startEditingPendingMessage,
    removePendingMessage,
    setEditMessageContent,
    getWorkspaceBlockingStatus,
    canSendMessage,
    fileInputRef,
    inputRef,
  } = useChat(workspaceData?.main_conversation_id ?? "", "lesson", handleNewMessageFromOthers);

  const debouncedOpenConversation = useDebouncedCallback(openConversation, 500);

  useEffect(() => {
    if (workspaceData?.main_conversation_id) {
      debouncedOpenConversation(workspaceData?.main_conversation_id);
    }
  }, [workspaceData?.main_conversation_id, debouncedOpenConversation]);

  // Clear unread messages when chat is opened
  useEffect(() => {
    if (isChatOpen && unreadMessageCount > 0) {
      clearUnreadMessageCount();
    }
  }, [isChatOpen, unreadMessageCount, clearUnreadMessageCount]);

  useEffect(() => {
    if (isChatOpen) {
      scrollToBottom(true);
    }
  }, [isChatOpen]);

  // Render chat content
  const renderChatContent = () => (
    <motion.div
      animate={{ opacity: 1 }}
      className="flex h-full max-h-full w-full flex-col"
      initial={{ opacity: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      {/* Chat messages */}
      <motion.div
        animate={{ y: 0, opacity: 1 }}
        className={`flex h-[calc(100%_-_70px)] flex-1 flex-col max-md:h-[calc(100%_-_90px)]`}
        initial={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.4, delay: 0.2, type: "spring", stiffness: 300, damping: 30 }}
      >
        {messages && (
          <ConversationView
            activeConversation={activeConversation}
            animatedMessageIds={animatedMessageIds}
            cancelEditing={cancelEditing}
            checkIfNearBottom={checkIfNearBottom}
            currentUser={user}
            deleteMessage={deleteMessage}
            disableMessageActions={canSendMessage().isWorkspaceBlocked}
            editMessageContent={editMessageContent}
            editTextareaRef={editTextareaRef}
            editingMessageId={editingMessageId}
            editingPendingMessageId={editingPendingMessageId}
            fetchNextPage={fetchNextPage}
            formatFileSize={formatFileSize}
            handleEditKeyPress={handleEditKeyPress}
            hasNextPage={hasNextPage}
            isCurrentUser={isCurrentUser}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            messages={messages}
            mode={"compact"}
            removePendingMessage={removePendingMessage}
            renderSpecialGroupsAsNormal={true}
            saveEditedMessage={saveEditedMessage}
            scrollAreaRef={scrollAreaRef}
            scrollToBottom={scrollToBottom}
            setEditMessageContent={setEditMessageContent}
            startEditingMessage={startEditingMessage}
            startEditingPendingMessage={startEditingPendingMessage}
          />
        )}
      </motion.div>

      {/* Chat input */}
      <motion.div
        animate={{ y: 0, opacity: 1 }}
        className={`${isMobile ? "p-3 pt-2" : "p-2 pt-1"}`}
        initial={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.4, delay: 0.3, type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Selected files preview */}
        <AnimatePresence>
          {selectedFilesCount > 0 && (
            <motion.div
              animate={{ opacity: 1, height: "auto", y: 0 }}
              className="mb-2 overflow-hidden"
              exit={{ opacity: 0, height: 0, y: -5 }}
              initial={{ opacity: 0, height: 0, y: -5 }}
              transition={{ duration: 0.25, type: "spring", stiffness: 500, damping: 30 }}
            >
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map((fileData, index) => (
                  <FilePreview
                    key={index}
                    file={fileData.file}
                    index={index}
                    isValid={fileData.isValid}
                    validationError={fileData.validationError}
                    onRemove={removeFile}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Workspace blocking indicator */}
        {(() => {
          const blockingStatus = getWorkspaceBlockingStatus();

          return (
            <WorkspaceBlockingIndicator
              className="mb-2"
              conversationType={activeConversation?.type || "group"}
              hasLeft={blockingStatus.hasLeft}
              isBlocked={blockingStatus.isBlocked}
            />
          );
        })()}

        {/* Only render input area if user can send messages */}
        {!canSendMessage().isWorkspaceBlocked && (
          <motion.div
            className={`bg-side-accent flex gap-2 rounded-lg shadow-inner transition-all focus-within:ring-2 focus-within:ring-gray-600 ${isMobile ? "p-3" : "me-1 p-2"}`}
            transition={{ duration: 0.2 }}
            whileHover={{ scale: isMobile ? 1 : 1.01 }}
          >
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              multiple
              className="hidden"
              type="file"
              onChange={handleFileChange}
            />

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className={`text-muted-foreground hover:text-accent-foreground hover:bg-side-accent/20 flex items-center justify-center rounded-full bg-transparent p-0 ${isMobile ? "h-12 w-12" : "h-10 w-10"}`}
                    disabled={isLoading || selectedFilesCount >= MAX_FILE_COUNT}
                    type="button"
                    variant="ghost"
                    onClick={handleAttachmentClick}
                  >
                    <Paperclip className={isMobile ? "h-6 w-6" : "h-5 w-5"} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("chat:attachments.attachFile")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Input
              ref={inputRef}
              className={`placeholder:text-muted-foreground flex-1 border-none bg-transparent text-white focus-visible:ring-0 focus-visible:ring-offset-0 ${isMobile ? "text-base" : ""}`}
              disabled={isLoading}
              placeholder={
                isSending ? t("chat:input.sendingPlaceholder") : t("chat:input.typingPlaceholder")
              }
              value={messageInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              onPaste={handlePaste}
            />

            {/* Emoji picker */}
            <EmojiPickerButton
              buttonClassName={`flex items-center rounded-full bg-transparent p-0 ${isMobile ? "h-12 w-12" : "h-10 w-6 w-8 sm:w-8"}`}
              onEmojiSelect={insertEmoji}
            />

            <Button
              className={`bg-transparent text-gray-400 transition-colors hover:bg-[#444654] ${isMobile ? "h-12 w-12" : ""}`}
              disabled={(!messageInput.trim() && selectedFilesCount === 0) || isLoading}
              size={isMobile ? "icon" : "default"}
              type="submit"
              onClick={sendMessage}
            >
              <Icon
                className={isSending ? "animate-spin" : ""}
                height={isMobile ? "24" : "20"}
                icon={isSending ? "lucide:loader-2" : "lucide:send"}
                width={isMobile ? "24" : "20"}
              />
              <span className="sr-only">Send</span>
            </Button>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );

  // For mobile, render as a dialog
  if (isMobile) {
    return (
      <>
        {/* Hidden chat that's always rendered for unread message tracking */}
        <div className="hidden">{renderChatContent()}</div>

        <Dialog open={isChatOpen} onOpenChange={setIsChatOpen}>
          <DialogContent
            className="bg-background h-[100dvh] max-h-[100dvh] w-[100dvw] max-w-none rounded-none p-0 backdrop-blur-sm"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <motion.div
              className="relative flex h-full max-h-[100dvh] flex-col"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <DragDropOverlay isDragging={isDragging} />
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="flex-shrink-0"
                initial={{ opacity: 0, y: -10 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                <DialogHeader className="border-border border-b px-4 py-3">
                  <div className="flex items-center justify-between">
                    <DialogTitle className="text-accent-foreground flex items-center gap-2 text-lg font-medium">
                      <Icon height="20" icon="lucide:message-square" width="20" />
                      {t("classroom:lessonChat")}
                    </DialogTitle>
                  </div>
                </DialogHeader>
              </motion.div>
              <div className="flex-1 overflow-hidden">{renderChatContent()}</div>
            </motion.div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // For desktop, render as a sidebar
  return (
    <>
      {/* Hidden chat that's always rendered for unread message tracking */}
      <div className="hidden">{renderChatContent()}</div>

      <AnimatePresence mode="wait">
        {isChatOpen && (
          <motion.div
            key="chat-sidebar"
            animate={{ opacity: 1, width: "100%" }}
            className="relative ms-2 h-full w-full overflow-hidden"
            exit={{ width: 0, opacity: 0 }}
            initial={{ width: 0, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              opacity: { duration: 0.2 },
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <DragDropOverlay isDragging={isDragging} />
            <motion.div
              animate={{ x: 0 }}
              className="bg-background/50 flex h-full flex-col rounded-l-xl backdrop-blur-sm"
              initial={{ x: 50 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 30 }}
            >
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="border-border flex items-center justify-between border-b p-2"
                initial={{ opacity: 0, y: -10 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                <h3 className="text-accent-foreground flex items-center gap-2 font-medium">
                  <Icon height="16" icon="lucide:message-square" width="16" />
                  {t("classroom:lessonChat")}
                </h3>
                <motion.div
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button
                    className="h-8 w-8 p-0"
                    size="icon"
                    variant="ghost"
                    onClick={() => setIsChatOpen(false)}
                  >
                    <Icon height="16" icon="lucide:x" width="16" />
                  </Button>
                </motion.div>
              </motion.div>
              <div className="h-[calc(100%_-_50px)] max-h-full flex-shrink">
                {renderChatContent()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
